import React from 'react';
import { Container } from 'reactstrap';
import {
  Route,
  Switch,
  RouteComponentProps,
  withRouter,
} from 'react-router-dom';
import Navbar from '../../containers/Navbar';
import { ProfileEdit, ProfileDashboard } from '../Profile';
import { OrganizationsDashboard } from '../Organization';
import {
  QuestionnaireView,
  QuestionnaireEdit,
  QuestionnaireNew,
} from '../Questionnaire';
import { PatientEdit, PatientDashboard } from '../Patient';
import { PractitionerDashboard, PractitionerEdit } from '../Practitioner';
import OrganizationView from '../Organization/View';
import PatientNew from '../Patient/New';
import PractitionerList from '../Practitioner/List';
import ReportDashboard from '../Report';
import InterviewLayout from '../Interview';
import { withAdalLoginApi, authContext } from '../../config/adal';
import ErrorView from '../Error';
import LoadingView from '../Loading';
import PractitionerNew from '../Practitioner/New';
import LandingView from '../Landing';
import MyChoices from '../MyChoices';
import UserProvider from '../../containers/Providers/User';
import { PractitionerContext } from '../../contexts/practitioner';
import UserNotFound from '../../containers/Cards/NoUserFound';
import { ModalProvider } from '../../modules/unsw-modals';

const createProtectedComponent =
  (
    Component: React.ComponentClass | React.StatelessComponent,
    requiredRoles: string[],
  ) =>
  (props: RouteComponentProps<{ organizationId?: string }>) =>
    (
      <UserProvider>
        <PractitionerContext.Consumer>
          {({ loading, practitioner, getRoles }) => {
            const active = practitioner && !!practitioner.active;
            if (loading) return null;
            if (!active) return <UserNotFound />;

            if (props.match.params.organizationId) {
              const roles = getRoles(props.match.params.organizationId).some(
                role =>
                  requiredRoles.some(
                    rRole => role.active && role.code === rRole,
                  ),
              );
              if (!roles) return <UserNotFound />;
            } else {
              // This seems like an abuse of the sec model. I'm not sure if this is how it should be. @Joshua Head
              const roles = getRoles(props.match.params.organizationId).some(
                role =>
                  requiredRoles.some(
                    rRole => role.active && role.code === rRole,
                  ),
              );
              if (!roles) return <UserNotFound />;
            }
            return <Component />;
          }}
        </PractitionerContext.Consumer>
      </UserProvider>
    );

const createProtectedRoute = (
  component: React.ComponentClass | React.StatelessComponent,
  requiredRoles: string[],
) =>
  withAdalLoginApi(
    withRouter(createProtectedComponent(component, requiredRoles)),
    () => <LoadingView />,
    error => {
      authContext.clearCache();
      return <ErrorView error={error} />;
    },
  );

const createProtectedNurseRoute = (
  component: React.ComponentClass | React.StatelessComponent,
) => createProtectedRoute(component, ['nurse', 'researcher', 'ict']);

const createProtectedAdminRoute = (
  component: React.ComponentClass | React.StatelessComponent,
) => createProtectedRoute(component, ['researcher', 'ict']);

const ProtectedReportDashboard = createProtectedAdminRoute(ReportDashboard);
const ProtectedProfileDashboard = createProtectedNurseRoute(ProfileDashboard);
const ProtectedProfileEdit = createProtectedNurseRoute(ProfileEdit);
const ProtectedOrganizationsDashboard = createProtectedNurseRoute(
  OrganizationsDashboard,
);
const ProtectedOrganizationView = createProtectedNurseRoute(OrganizationView);
const ProtectedPractitionerDashboard = createProtectedAdminRoute(
  PractitionerDashboard,
);
const ProtectedPractitionerList = createProtectedAdminRoute(PractitionerList);
const ProtectedPractitionerEdit = createProtectedAdminRoute(PractitionerEdit);
const ProtectedPractitionerNew = createProtectedAdminRoute(PractitionerNew);
const ProtectedPatientDashboard = createProtectedNurseRoute(PatientDashboard);
const ProtectedPatientNew = createProtectedNurseRoute(PatientNew);
const ProtectedPatientEdit = createProtectedNurseRoute(PatientEdit);
const ProtectedInterviewLayout = createProtectedNurseRoute(InterviewLayout);
const ProtectedQuestionnaireView = createProtectedNurseRoute(QuestionnaireView);
const ProtectedQuestionnaireEdit = createProtectedNurseRoute(QuestionnaireEdit);
const ProtectedQuestionnaireNew = createProtectedNurseRoute(QuestionnaireNew);
const ProtectedMyChoices = createProtectedNurseRoute(MyChoices);

const RootDashboard = () => (
  <Container fluid className="p-0">
    <ModalProvider>
      <Navbar />
      <Switch>
        <Route path="/" exact component={LandingView}></Route>
        <Route path="/report" component={ProtectedReportDashboard}></Route>
        <Route path="/profile" component={ProtectedProfileDashboard}></Route>
        <Route path="/profile/edit" component={ProtectedProfileEdit}></Route>
        <Route
          exact
          path={['/organization']}
          component={ProtectedOrganizationsDashboard}></Route>
        <Route
          exact
          path="/organization/:organizationId"
          component={ProtectedOrganizationView}></Route>
        <Route
          exact
          path="/organization/:organizationId/practitioner"
          component={ProtectedPractitionerList}
        />
        <Route
          exact
          path="/organization/:organizationId/practitioner/:practitionerId"
          component={ProtectedPractitionerDashboard}></Route>
        <Route
          exact
          path="/organization/:organizationId/practitioner/:practitionerId/edit"
          component={ProtectedPractitionerEdit}></Route>
        <Route
          exact
          path="/organization/:organizationId/newPractitioner"
          component={ProtectedPractitionerNew}></Route>
        <Route
          exact
          path="/organization/:organizationId/newPatient"
          component={ProtectedPatientNew}></Route>
        <Route
          exact
          path="/organization/:organizationId/patient/:patientId/"
          component={ProtectedPatientDashboard}></Route>
        <Route
          exact
          path="/organization/:organizationId/patient/:patientId/edit"
          component={ProtectedPatientEdit}></Route>
        <Route
          exact
          path="/organization/:organizationId/patient/:patientId/interview"
          component={ProtectedInterviewLayout}></Route>
        <Route
          exact
          path="/organization/:organizationId/patient/:patientId/myChoices/"
          component={ProtectedMyChoices}></Route>
        <Route
          exact
          path="/organization/:organizationId/patient/:patientId/questionnaireResponse/:questionnaireResponseId"
          component={ProtectedQuestionnaireView}></Route>
        <Route
          exact
          path="/organization/:organizationId/patient/:patientId/questionnaireResponse/:questionnaireResponseId/edit"
          component={ProtectedQuestionnaireEdit}></Route>
        <Route
          exact
          path="/organization/:organizationId/patient/:patientId/newQuestionnaireResponse/:questionnaireName"
          component={ProtectedQuestionnaireNew}></Route>
      </Switch>
    </ModalProvider>
  </Container>
);

export default RootDashboard;
